import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Send, MessageCircle, ArrowRight, Info } from "lucide-react";

const MODES = [
  { id: "vent", label: "I want to vent", desc: "Let it all out. No judgments, no advice unless you ask.", color: "#C86B55" },
  { id: "plan", label: "I want a plan", desc: "Help me figure out my next steps. Let's think together.", color: "#4A6B53" },
  { id: "general", label: "Just talk", desc: "No agenda. Just be here with me.", color: "#D2A679" },
];

const QUICK_PROMPTS = [
  "I'm feeling overwhelmed today",
  "I need to talk about something",
  "I'm not sure what I'm feeling",
  "Something happened and I need support",
  "I want to feel better",
  "Help me make sense of things",
];

const MODE_OPENERS = {
  vent: "I'm here. This is your space — let it all out. Whatever you're feeling, I'm listening.",
  plan: "Okay, let's figure this out together. What's the situation? We'll take it one step at a time.",
  general: "Hey. I'm glad you're here. What's on your mind?",
};

export default function TalkPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState(null);
  const [sending, setSending] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);

  // Show session notice once
  useEffect(() => {
    if (!sessionStorage.getItem("sovha_chat_notice_seen")) {
      setShowNotice(true);
      sessionStorage.setItem("sovha_chat_notice_seen", "true");
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text) => {
    const content = text || input.trim();
    if (!content || sending) return;
    setError("");
    setSending(true);
    setInput("");

    const userMsg = { id: Date.now(), role: "user", content, timestamp: new Date().toISOString() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);

    try {
      const res = await api.post(`/chat/stateless`, {
        content,
        mode: mode || "general",
        messages: newMessages,
      });
      const sovhaMsg = { id: Date.now() + 1, role: "sovha", content: res.data.response, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, sovhaMsg]);
    } catch (e) {
      setError("Something went wrong. Please try again.");
      console.error(e);
    }
    setSending(false);
  };

  const selectMode = (modeId) => {
    setMode(modeId);
    // Add Sovha's opening message for this mode
    const opener = MODE_OPENERS[modeId] || MODE_OPENERS.general;
    setMessages([{ id: Date.now(), role: "sovha", content: opener, timestamp: new Date().toISOString() }]);
  };

  // Mode Picker
  if (!mode) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-16 sm:py-24" data-testid="talk-page">
        <div className="text-center mb-10">
          <p className="text-terracotta text-sm uppercase tracking-[0.15em] font-bold mb-3">Talk to Sovha</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-earth-800 mb-2 tracking-tight">Hey, I'm here</h1>
          <p className="text-earth-500 text-sm">Before we start, what feels right today?</p>
        </div>

        {showNotice && (
          <div className="flex items-start gap-3 bg-sage-light/50 rounded-2xl p-4 mb-8 max-w-md mx-auto" data-testid="chat-privacy-notice">
            <Info size={16} className="text-sage mt-0.5 flex-shrink-0" />
            <p className="text-earth-600 text-sm">This conversation is private and won't be saved. It ends when you leave this page.</p>
          </div>
        )}

        <div className="space-y-4" data-testid="mode-picker">
          {MODES.map((m) => (
            <button key={m.id} onClick={() => selectMode(m.id)} data-testid={`mode-pick-${m.id}`}
              className="w-full text-left bg-white rounded-[2rem] p-6 sm:p-8 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 hover:shadow-[0_12px_40px_rgba(45,49,46,0.08)] hover:-translate-y-1 transition-all duration-500 group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: m.color + "15" }}>
                  <MessageCircle size={22} style={{ color: m.color }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl text-earth-800 mb-1">{m.label}</h3>
                  <p className="text-earth-500 text-sm">{m.desc}</p>
                </div>
                <ArrowRight size={18} className="text-earth-300 group-hover:text-earth-600 transition-colors" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const modeInfo = MODES.find(m => m.id === mode);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12 flex flex-col" style={{ minHeight: "calc(100vh - 12rem)" }} data-testid="talk-page">
      {/* Header */}
      <div className="text-center mb-4">
        <p className="text-terracotta text-sm uppercase tracking-[0.15em] font-bold mb-2">Talk to Sovha</p>
        {modeInfo && (
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: modeInfo.color + "15", color: modeInfo.color }}>
            {modeInfo.label}
          </span>
        )}
      </div>

      {/* Privacy notice */}
      {showNotice && (
        <div className="flex items-start gap-3 bg-sage-light/50 rounded-2xl p-3 mb-4 mx-auto max-w-md" data-testid="chat-privacy-notice">
          <Info size={14} className="text-sage mt-0.5 flex-shrink-0" />
          <p className="text-earth-500 text-xs">This conversation is private and won't be saved. It ends when you leave this page.</p>
        </div>
      )}

      {/* Chat Area */}
      <div className="flex-1 bg-white rounded-[2rem] shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages" style={{ maxHeight: "50vh", minHeight: "300px" }}>
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full">
              <MessageCircle size={40} className="text-earth-300 mb-4" />
              <p className="text-earth-500 text-center mb-6">Pick a prompt or type what's on your mind.</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-md">
                {QUICK_PROMPTS.map((p) => (
                  <button key={p} onClick={() => sendMessage(p)} data-testid="quick-prompt"
                    className="bg-terracotta-light text-terracotta px-4 py-2 rounded-full text-sm font-medium hover:bg-terracotta/10 transition-all duration-300">{p}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} chat-bubble-enter`}>
              <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${msg.role === "user" ? "bg-terracotta-light text-earth-800" : "bg-sage-light text-earth-800"}`}>
                {msg.role === "sovha" && <p className="text-xs font-semibold text-sage mb-1">Sovha</p>}
                <p className="text-sm leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-sage-light rounded-2xl px-5 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-sage/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-sage/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-sage/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="flex justify-center">
              <p className="text-terracotta text-sm bg-terracotta-light px-4 py-2 rounded-full">{error}</p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-earth-200/50 p-4 flex items-center gap-3">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder="Type what's on your mind..."
            data-testid="chat-input" className="flex-1 bg-earth-100 border-0 rounded-2xl px-5 py-3 text-earth-800 focus:ring-2 focus:ring-terracotta/30 focus:outline-none placeholder:text-earth-400 text-sm" />
          <button onClick={() => sendMessage()} disabled={!input.trim() || sending} data-testid="chat-send-btn"
            className="bg-terracotta text-earth-50 rounded-full p-3 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 disabled:opacity-40 disabled:hover:translate-y-0"><Send size={18} /></button>
        </div>
      </div>
    </div>
  );
}
